import Avatar from "../components/ds/Avatar";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import { fieldClass } from "../components/ds/Field";
import Icon from "../components/ds/Icon";
import InlineMessage from "../components/ds/InlineMessage";
import { MenuItem } from "../components/ds/Menu";
import PageHeader from "../components/ds/PageHeader";
import SearchField from "../components/ds/SearchField";
import Switch from "../components/ds/Switch";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiPlus,
  FiTrash2,
  FiBriefcase,
  FiUsers,
  FiCpu,
  FiCalendar,
  FiMail,
  FiPackage,
  FiHome,
  FiBookOpen,
  FiCheck,
} from "react-icons/fi";
import { createMintSpace } from "../api/mintSpace";
import { getBusinessPrefill } from "../api/kyc";
import { BUSINESS_VERIFICATION_ENABLED, emptyVerification } from "../constants/kyc";
import ApplicantTypePicker from "../components/kyc/ApplicantTypePicker";
import {
  KybActivitySection,
  KybCompanySection,
  KybPeopleSection,
} from "../components/kyc/KybForm";
import KycForm from "../components/kyc/KycForm";
import { SectionTitle } from "../components/kyc/KycFields";
import {
  VERIFICATION_STEPS,
  clearErrorsAt,
  serverFieldErrors,
  setIn,
  validateVerification,
  verificationPayload,
  verificationStepOf,
  verificationToForm,
} from "../utils/kyc";
import { useTranslation } from "../context/TranslationContext";
import {
  BUSINESS_TYPE_GROUPS,
  encodeCategory,
  getBusinessTypeLabel,
  getBusinessNicheLabel,
  getNichesForType,
  getBusinessCategoryLabel,
} from "../constants/businessCategories";

/**
 * First who's creating the business — an individual or a company — then the business
 * itself, then its verification (KYB) form: an individual's on one step, a company's
 * over three. Every new business fills it in, and an admin then reviews it. Until the
 * first choice is made, the form's steps are one placeholder.
 *
 * With verification switched off, it's the business alone. @see BUSINESS_VERIFICATION_ENABLED
 */
const stepsFor = (applicantType) =>
  BUSINESS_VERIFICATION_ENABLED
    ? ["owner", "business", ...(VERIFICATION_STEPS[applicantType] || ["verification"])]
    : ["business"];

const STEP_LABEL_KEYS = {
  owner: "createBusiness.stepOwner",
  business: "createBusiness.stepBusiness",
  verification: "createBusiness.stepVerification",
  details: "createBusiness.stepDetails",
  company: "createBusiness.stepCompany",
  activity: "createBusiness.stepActivity",
  people: "createBusiness.stepPeople",
};

const TYPE_ICONS = {
  "coaching-courses": FiBookOpen,
  "agency-services": FiBriefcase,
  "paid-groups": FiUsers,
  "software-saas": FiCpu,
  events: FiCalendar,
  newsletters: FiMail,
  "physical-products": FiPackage,
  "brick-and-mortar": FiHome,
};

function initials(name) {
  const p = String(name || "?")
    .trim()
    .split(/\s+/);
  if (p.length >= 2) return (p[0][0] + p[1][0]).toUpperCase();
  return (p[0]?.[0] || "?").toUpperCase();
}

function LivePreview({ name, businessType, niche, t }) {
  const categoryPreview =
    businessType && niche
      ? getBusinessCategoryLabel(t, encodeCategory(businessType, niche))
      : businessType
      ? getBusinessTypeLabel(t, businessType)
      : null;

  return (
    <div className="flex flex-col items-center gap-space-12 rounded-radius-lg bg-bg-raised p-space-16 text-center">
      <p className="w-full text-left text-caps-label uppercase text-accent-text">{t("createBusiness.previewLabel")}</p>
      <Avatar size="l" name={name || t("createBusiness.previewEmpty")} initials={initials(name || t("createBusiness.previewEmpty"))} />
      <h3 className="max-w-full truncate text-headline text-label-primary">
        {name.trim() || t("createBusiness.previewEmpty")}
      </h3>
      <p className={`line-clamp-2 text-footnote ${categoryPreview ? "text-label-secondary" : "text-label-tertiary"}`}>
        {categoryPreview || t("createBusiness.previewHint")}
      </p>
      <Badge>{t("createBusiness.previewOwner")}</Badge>
    </div>
  );
}

function FieldShell({ label, required, error, children, isDark }) {
  return (
    <div className="space-y-1.5">
      <label
        className="block text-footnote-semibold text-label-secondary"
      >
        {label} {required && <span className="text-status-danger">*</span>}
      </label>
      {children}
      {error && <p className="text-footnote text-status-danger">{error}</p>}
    </div>
  );
}

function CategorySelect({ value, options, onChange, isDark, t, error, typeLabel }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const showSearch = options.length > 8;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((nicheId) =>
      getBusinessNicheLabel(t, nicheId).toLowerCase().includes(q)
    );
  }, [options, search, t]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return undefined;
    }
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    const tId = window.setTimeout(() => searchRef.current?.focus(), 50);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      window.clearTimeout(tId);
    };
  }, [open]);

  const selectedLabel = value ? getBusinessNicheLabel(t, value) : null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={fieldClass({
          tone: error ? "error" : "default",
          className: "gap-space-8 px-space-16 text-left text-callout",
        })}
      >
        <span className={`min-w-0 flex-1 truncate ${selectedLabel ? "text-label-primary" : "text-label-tertiary"}`}>
          {selectedLabel || t("createBusiness.categoryPlaceholder")}
          {typeLabel && <span className="text-label-tertiary"> · {typeLabel}</span>}
        </span>
        <Icon name={open ? "chevron-up" : "chevron-down"} size={20} className="shrink-0 text-label-tertiary" />
      </button>

      {open && (
        <div className="absolute inset-x-0 z-50 mt-space-8 flex max-h-[536px] flex-col rounded-radius-lg border border-separator-default bg-bg-raised p-space-8 shadow-overlay animate-ds-pop-in">
          {showSearch && (
            <div className="p-space-4">
              <SearchField
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("createBusiness.searchCategory")}
              />
            </div>
          )}
          <div role="listbox" className="min-h-0 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-space-12 py-space-24 text-center text-footnote text-label-tertiary">
                {t("createBusiness.noCategoryResults")}
              </p>
            ) : (
              filtered.map((nicheId) => {
                const selected = value === nicheId;
                return (
                  <MenuItem
                    key={nicheId}
                    role="option"
                    aria-selected={selected}
                    selected={selected}
                    detail={selected ? <Icon name="check" size={20} className="shrink-0 text-accent-text" /> : null}
                    onClick={() => {
                      onChange(nicheId);
                      setOpen(false);
                    }}
                  >
                    {getBusinessNicheLabel(t, nicheId)}
                  </MenuItem>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const CreateMintSpacePage = ({ isDark, setToast, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    businessType: "",
    niche: "",
    enableMemberQuestions: false,
    questions: [""],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [kyb, setKyb] = useState(emptyVerification);
  const [kybErrors, setKybErrors] = useState({});
  const kybTouched = useRef(false);
  const scrollRef = useRef(null);
  const stepsRef = useRef(null);

  const steps = stepsFor(kyb.applicantType);
  const currentStep = steps[step];

  // Start the verification form from what the account already says — or, for a second
  // business, from the last one's form — unless the owner has already started typing.
  useEffect(() => {
    if (!BUSINESS_VERIFICATION_ENABLED) return undefined;
    let alive = true;
    getBusinessPrefill()
      .then((res) => {
        if (alive && res?.data && !kybTouched.current) {
          setKyb((prev) => ({ ...verificationToForm(res.data), applicantType: prev.applicantType }));
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const changeKyb = (path, value) => {
    // Choosing individual or company isn't typing — a prefill arriving after it still applies.
    if (path !== "applicantType") kybTouched.current = true;
    setKyb((prev) => setIn(prev, path, value));
    setKybErrors((prev) => clearErrorsAt(prev, path));
  };

  const niches = useMemo(
    () => (formData.businessType ? getNichesForType(formData.businessType) : []),
    [formData.businessType]
  );

  const selectBusinessType = (typeId) => {
    setFormData((prev) => ({
      ...prev,
      businessType: typeId,
      niche: "",
    }));
    setErrors((prev) => ({ ...prev, businessType: undefined, niche: undefined }));
  };

  const selectNiche = (nicheId) => {
    setFormData((prev) => ({ ...prev, niche: nicheId }));
    setErrors((prev) => ({ ...prev, niche: undefined }));
  };

  const questionsToSend = () =>
    formData.enableMemberQuestions
      ? (formData.questions || []).map((q) => q.trim()).filter(Boolean)
      : [];

  /** The first step: name, type, category and join questions. */
  const basicsErrors = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("mintSpace.nameRequired") || "Name is required";
    }
    if (!formData.businessType) {
      newErrors.businessType =
        t("createBusiness.businessTypeRequired") || "Business type is required";
    }
    if (!formData.niche) {
      newErrors.niche = t("createBusiness.nicheRequired") || "Category is required";
    }
    if (formData.enableMemberQuestions && questionsToSend().length === 0) {
      newErrors.questions =
        t("createBusiness.questionsRequired") || "Add at least one question";
    }
    return newErrors;
  };

  const goToStep = (index) => {
    setStep(index);
    scrollRef.current?.scrollTo({ top: 0 });
    // On a phone the page scrolls rather than the form, so bring the next step's top into view.
    stepsRef.current?.scrollIntoView({ block: "nearest" });
  };

  /** Show the step holding the first error, with every error marked. */
  const showKybErrors = (found) => {
    setKybErrors(found);
    const stepOf = (path) =>
      path === "applicantType" ? "owner" : verificationStepOf(kyb.applicantType, path);
    const firstStep = steps.find((s) => Object.keys(found).some((path) => stepOf(path) === s));
    if (firstStep) goToStep(steps.indexOf(firstStep));
    setToast(t("kyc.errors.fixBelow"));
  };

  const goNext = () => {
    if (currentStep === "owner") {
      if (!kyb.applicantType) {
        setKybErrors({ applicantType: t("kyc.errors.applicantType") });
        return;
      }
    } else if (currentStep === "business") {
      const found = basicsErrors();
      setErrors(found);
      if (Object.keys(found).length) return;
      // The business's name is the natural brand name.
      setKyb((prev) => (prev.brandName.trim() ? prev : { ...prev, brandName: formData.name.trim() }));
    } else {
      const found = validateVerification(kyb, t, [currentStep]);
      setKybErrors(found);
      if (Object.keys(found).length) return;
    }
    goToStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < steps.length - 1) {
      goNext();
      return;
    }

    const newErrors = basicsErrors();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      goToStep(steps.indexOf("business"));
      return;
    }
    const kybFound = BUSINESS_VERIFICATION_ENABLED ? validateVerification(kyb, t) : {};
    if (Object.keys(kybFound).length > 0) {
      showKybErrors(kybFound);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createMintSpace({
        name: formData.name.trim(),
        category: encodeCategory(formData.businessType, formData.niche),
        questions: questionsToSend(),
        ...(BUSINESS_VERIFICATION_ENABLED ? { kyb: verificationPayload(kyb) } : {}),
      });
      if (result.success) {
        setToast(
          t("mintSpace.mintSpaceCreatedSuccessfully") || "Business created successfully!"
        );
        onSuccess?.(result.data);
      }
    } catch (error) {
      const fieldErrors = serverFieldErrors(error);
      if (fieldErrors) {
        showKybErrors(fieldErrors);
      } else {
        setToast(
          error.message || t("mintSpace.errorCreatingMintSpace") || "Error creating business"
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBase = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";

  return (
    // Below lg the page scrolls as a whole: squeezed under the preview, the form would get
    // no height of its own on a phone. From lg up, the form scrolls inside its card.
    <div className="flex h-full min-h-0 flex-col gap-space-24 overflow-y-auto px-2 py-3 font-ds sm:px-3 lg:overflow-hidden lg:py-4">
      <PageHeader
        eyebrow={t("createBusiness.heroBadge")}
        title={`${t("createBusiness.heroTitle")} ${t("createBusiness.heroAccent")}`}
        subtitle={t("createBusiness.subtitle")}
      />
      <div className="flex flex-col gap-space-24 lg:min-h-0 lg:flex-1 lg:flex-row lg:items-start">
        <div className="flex w-full shrink-0 flex-col gap-space-24 lg:w-[300px]">
          <LivePreview name={formData.name} businessType={formData.businessType} niche={formData.niche} t={t} />
          {BUSINESS_VERIFICATION_ENABLED && (
            <div className="hidden rounded-radius-lg bg-bg-raised p-space-16 lg:block">
              <InlineMessage icon="lock" title={t("createBusiness.verificationTitle")}>
                {t("createBusiness.verificationNote")}
              </InlineMessage>
            </div>
          )}
        </div>

        {/* Right — form */}
        <form
          onSubmit={handleSubmit}
          noValidate
          className="flex min-w-0 flex-1 flex-col rounded-radius-lg bg-bg-raised p-space-16 lg:max-h-full lg:min-h-0 lg:overflow-hidden"
        >
          {steps.length > 1 && (
          <ol ref={stepsRef} className="mb-4 flex shrink-0 scroll-mt-3 items-center gap-1.5" aria-label={t("createBusiness.stepOf", { n: step + 1, total: steps.length })}>
            {steps.map((id, index) => {
              const done = index < step;
              const current = index === step;
              return (
                <li key={id} className="flex min-w-0 flex-1 items-center gap-1.5">
                  <button
                    type="button"
                    disabled={!done}
                    onClick={() => goToStep(index)}
                    aria-current={current ? "step" : undefined}
                    className={`flex min-w-0 items-center gap-1.5 rounded-radius-xs py-1 pr-1.5 text-left transition ${
                      done ? "cursor-pointer hover:opacity-80" : "cursor-default"
                    }`}
                  >
                    <span
                      className={`grid size-6 shrink-0 place-items-center rounded-full text-caption-1-semibold ${
                        current || done
                          ? "bg-accent-default text-accent-on"
                          : "bg-fill-secondary text-label-secondary"
                      }`}
                    >
                      {done ? <FiCheck className="size-3.5" /> : index + 1}
                    </span>
                    <span
                      className={`hidden truncate text-footnote-semibold sm:block ${
                        current ? ("text-label-primary") : "text-label-tertiary"
                      }`}
                    >
                      {t(STEP_LABEL_KEYS[id])}
                    </span>
                  </button>
                  {index < steps.length - 1 && (
                    <span className={`h-px min-w-3 flex-1 ${done ? "bg-accent-default" : "bg-separator-default"}`} />
                  )}
                </li>
              );
            })}
          </ol>
          )}

          <div ref={scrollRef} className="space-y-3 pr-0.5 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
            {currentStep === "owner" && (
              <section>
                <SectionTitle title={t("kyc.applicant.title")} hint={t("kyc.applicant.hint")} isDark={isDark} />
                <ApplicantTypePicker
                  value={kyb.applicantType}
                  onChange={(v) => changeKyb("applicantType", v)}
                  error={kybErrors.applicantType}
                  isDark={isDark}
                  t={t}
                />
              </section>
            )}

            {currentStep === "business" && (
              <>
              <FieldShell
                label={t("createBusiness.nameLabel") || "Business name"}
                required
                error={errors.name}
                isDark={isDark}
              >
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className={`w-full rounded-radius-sm border px-3 py-2.5 text-subheadline transition ${inputBase} ${
                    errors.name ? "border-status-danger" : ""
                  } focus:outline-none focus:ring-2 focus:ring-accent-default`}
                  placeholder={t("createBusiness.namePlaceholder") || "Enter your business name"}
                />
              </FieldShell>

              <FieldShell
                label={t("createBusiness.businessTypeLabel") || "Business type"}
                required
                error={errors.businessType}
                isDark={isDark}
              >
                <div className="flex flex-wrap gap-space-8">
                  {BUSINESS_TYPE_GROUPS.map((group) => {
                    const Icon = TYPE_ICONS[group.id] || FiBriefcase;
                    const selected = formData.businessType === group.id;
                    return (
                      <button
                        key={group.id}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => selectBusinessType(group.id)}
                        className={`flex items-center gap-space-12 rounded-[22px] p-space-16 text-left outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${
                          selected
                            ? "bg-accent-tinted text-accent-tinted-label shadow-[inset_0_0_0_1px_var(--color-accent-tinted-label)]"
                            : "bg-fill-quaternary text-label-primary hover:bg-fill-tertiary"
                        }`}
                      >
                        <Icon className="size-5 shrink-0" />
                        <span className="text-subheadline-semibold">{getBusinessTypeLabel(t, group.id)}</span>
                      </button>
                    );
                  })}
                </div>
              </FieldShell>

              {formData.businessType && (
                <FieldShell
                  label={t("createBusiness.categoryLabel") || "Category"}
                  required
                  error={errors.niche}
                  isDark={isDark}
                >
                  <CategorySelect
                    key={formData.businessType}
                    value={formData.niche}
                    options={niches}
                    onChange={selectNiche}
                    isDark={isDark}
                    t={t}
                    error={errors.niche}
                    typeLabel={
                      t("createBusiness.categoriesForType", {
                        type: getBusinessTypeLabel(t, formData.businessType),
                      }) ||
                      `Categories for ${getBusinessTypeLabel(t, formData.businessType)}`
                    }
                  />
                </FieldShell>
              )}

              <div>
                <div className="flex items-center gap-space-12 rounded-[22px] border border-separator-default p-space-16">
                  <span className="flex min-w-0 flex-1 flex-col gap-space-4">
                    <span className="text-subheadline-semibold text-label-primary">{t("createBusiness.memberQuestionsToggle")}</span>
                    <span className="text-footnote text-label-secondary">{t("createBusiness.memberQuestionsHelp")}</span>
                  </span>
                  <Switch
                    on={formData.enableMemberQuestions}
                    label={t("createBusiness.memberQuestionsToggle")}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        enableMemberQuestions: !prev.enableMemberQuestions,
                        questions: !prev.enableMemberQuestions && !prev.questions?.length ? [""] : prev.questions,
                      }))
                    }
                  />
                </div>

                <AnimatePresence>
                  {formData.enableMemberQuestions && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-space-8 space-y-1.5">
                        {(formData.questions || []).map((q, idx) => (
                          <div key={idx} className="flex gap-2">
                            <input
                              type="text"
                              value={q}
                              onChange={(e) => {
                                const next = [...formData.questions];
                                next[idx] = e.target.value;
                                setFormData((prev) => ({ ...prev, questions: next }));
                              }}
                              maxLength={300}
                              placeholder={
                                t("createBusiness.questionPlaceholder", { n: idx + 1 }) ||
                                `Question ${idx + 1}`
                              }
                              className={`min-w-0 flex-1 rounded-radius-sm border px-3 py-2 text-subheadline ${inputBase}`}
                            />
                            {(formData.questions?.length || 0) > 1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    questions: prev.questions.filter((_, i) => i !== idx),
                                  }))
                                }
                                className={`grid size-9 shrink-0 place-items-center rounded-radius-sm transition ${
                                  "bg-fill-tertiary hover:bg-fill-quaternary"
                                }`}
                                aria-label={t("common.remove") || "Remove"}
                              >
                                <FiTrash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                        {(formData.questions?.length || 0) < 10 && (
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                questions: [...(prev.questions || []), ""],
                              }))
                            }
                            className="inline-flex items-center gap-1.5 text-footnote-semibold text-accent-text hover:underline"
                          >
                            <FiPlus className="h-3.5 w-3.5" />
                            {t("createBusiness.addQuestion") || "Add question"}
                          </button>
                        )}
                        {errors.questions && (
                          <p className="text-footnote text-status-danger">{errors.questions}</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              </>
            )}

            {currentStep === "details" && (
              <KycForm form={kyb} onChange={changeKyb} errors={kybErrors} isDark={isDark} t={t} />
            )}
            {currentStep === "company" && (
              <KybCompanySection form={kyb} onChange={changeKyb} errors={kybErrors} isDark={isDark} t={t} />
            )}
            {currentStep === "activity" && (
              <KybActivitySection form={kyb} onChange={changeKyb} errors={kybErrors} isDark={isDark} t={t} />
            )}
            {currentStep === "people" && (
              <KybPeopleSection form={kyb} onChange={changeKyb} errors={kybErrors} isDark={isDark} t={t} />
            )}
          </div>

          <div className="mt-space-16 flex shrink-0 items-center gap-space-8">
            {step > 0 && (
              <Button
                variant="gray"
                icon={<Icon name="arrow-left" size={20} />}
                disabled={isSubmitting}
                onClick={() => goToStep(step - 1)}
              >
                {t("createBusiness.back")}
              </Button>
            )}
            <Button type="submit" fullWidth className="flex-1" loading={isSubmitting}>
              {isSubmitting
                ? t("createBusiness.creating")
                : step < steps.length - 1
                ? t("createBusiness.continue")
                : t("createBusiness.submit")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMintSpacePage;
